import { ApolloQueryResult, gql, useApolloClient } from '@apollo/client';
import { useState, useEffect } from 'react';

// Anonymous public report path: select no user PII (email, phone, invite
// link) — the report only renders id/name/avatar. TA-11099.
export const GET_TASK_ACTIVITY_LOG = gql`
  query getProjectTaskActivityReport($uuid: ID!, $taskId: ID!, $limit: Int) {
    getProjectTaskActivityReport(uuid: $uuid, taskId: $taskId, limit: $limit) {
      createdAt
      event
      id
      user {
        id
        name
        firstName
        lastName
        avatar {
          ...FileDetails
        }
      }
      attachment {
        ... on Document {
          id
          name
          contentType
          createdAt
          size
          isImage
          clientId
          isAudio
          duration

          file {
            id
            url
            cdnBaseUrl
            path
            isPreviewable
            thumbnail
          }
        }
        ... on Message {
          id
          chatId
          clientId
          body
          createdAt
          publishedAt
          authorId
          author {
            id
            name
          }
          attachments {
            ... on MessageTask {
              id
              authorId
              author {
                id
                name
                avatar {
                  ...FileDetails
                }
              }
              task {
                id
                projectId
                deletedAt
                name
              }
            }
            ... on MessageProject {
              id
              authorId
              author {
                id
                name
                avatar {
                  ...FileDetails
                }
              }
              project {
                id
                name
              }
            }
            ... on Document {
              id
              name
              contentType
              createdAt
              size
              isImage
              clientId
              isAudio
              duration
              file {
                ...FileDetails
              }
            }
            ... on MessageTaskAssignment {
              id
              messageId
              assignees {
                id
                name
                avatar {
                  id
                  url
                  cdnBaseUrl
                  path
                  thumbnail
                }
              }
              task {
                id
                name
                deletedAt
                completedAt
              }
            }
            ... on MessageTaskComplete {
              id
              messageId
              completor {
                id
                name
                avatar {
                  id
                  url
                  cdnBaseUrl
                  path
                  thumbnail
                }
              }
            }
          }
        }
        ... on EntityUser {
          id
          user {
            id
            name
            firstName
            lastName
            avatar {
              ...FileDetails
            }
          }
          entityTask: task {
            id
            name
            deletedAt
            members {
              id
              role
              user {
                id
                name
                firstName
                lastName
                avatar {
                  ...FileDetails
                }
              }
            }
            project {
              id
              name
            }
          }
          entityProject: project {
            id
            name
          }
        }
      }
    }
  }
`;

// Custom hook to fetch activity logs for multiple tasks

const useActivityLogsForTasks = (uuid: string, taskIds: Array<string>) => {
  const client = useApolloClient();
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  interface Item {
    event: string;
  }
  interface Result {
    getProjectTaskActivityReport: Array<Item>;
  }

  function hasPhotos(result: ApolloQueryResult<Result>) {
    return result?.data?.getProjectTaskActivityReport?.some(
      (item: { event: string }) => item.event === 'TaskDocument'
    );
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const promises = taskIds.map(async (taskId) => {
          const result = await client.query({
            query: GET_TASK_ACTIVITY_LOG,
            variables: { uuid, taskId },
          });

          const containPhotos = hasPhotos(result);

          return {
            task_id: taskId,
            logs: result?.data?.getProjectTaskActivityReport || [],
            containPhotos,
            error: result?.error,
          };
        });

        const resolvedLogs = await Promise.allSettled(promises);
        setActivityLogs(resolvedLogs?.map((item) => item?.value));
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Unknown error occurred')
        );
      } finally {
        setLoading(false);
      }
    };

    if (uuid && taskIds.length > 0) {
      fetchData();
    }
  }, [uuid, taskIds?.length]);

  return { activityLogs, loading, error };
};

export default useActivityLogsForTasks;
